/**
 * Case tab feature — case form sync/save, saved-case CRUD, elements rendering,
 * header, and sub-tab switching.
 *
 * Faithful port of the monolith's syncCaseForm / saveCase / newCase /
 * deleteSelectedCase / loadSavedCase / renderElements / switchCaseTab. The
 * monolith global `state` becomes `store.getState()` and `saveState()` becomes
 * `store.save()`.
 *
 * openCaseEdit() is deliberately absent: it was `showPage('case')` behind a
 * header button that duplicated the always-visible bottom-nav Case tab, so both
 * were removed rather than kept in sync.
 */

import { store } from '@/app/store';
import type { SavedCase } from '@/types/state';
import type { ChargeDescription } from '@/types/charge';
import { byId, inputValue, setInputValue } from '@/ui/dom';
import { showToast, confirmApp } from '@/ui/overlays';
import { escapeHTML } from '@/utils/formatters';

/**
 * Cross-feature helpers referenced by the Case tab but owned by not-yet-ported
 * modules: `renderChargesList` (charges feature — renders state.chargeList) and
 * `applyCitationLinks` (citations UI decorator). Invoked defensively via the
 * global registry so this module compiles and no-ops until those land. See the
 * report notes accompanying this port.
 */
function callGlobal(name: string, ...args: unknown[]): void {
  const fn = (window as unknown as Record<string, unknown>)[name];
  if (typeof fn === 'function') {
    (fn as (...a: unknown[]) => void)(...args);
  }
}

/** Coerce an index-signature (unknown) saved-case field to a string. */
function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/** Populate the Case form fields from state. */
export function syncCaseForm(): void {
  const s = store.getState();
  setInputValue('inp-case-name', s.caseName);
  setInputValue('inp-court', s.court);
  setInputValue('inp-next-event', s.trialDate);
  setInputValue('inp-judge', s.judge);
  setInputValue('inp-opp', s.opp);
  setInputValue('inp-defense-atty', s.defenseAtty || '');
  setInputValue('inp-case-notes', s.caseNotes);
  setInputValue('inp-docket', s.docket || '');
  setInputValue('inp-county', s.county || 'Suffolk');
  setInputValue('inp-pros-bbo', s.prossBBO || '');
  setInputValue('inp-def-bbo', s.defBBO || '');
  setInputValue('inp-indictment-facts', s.indictmentFacts || '');
  setInputValue('inp-charge-description', s.chargeDescription || '');
  setInputValue('inp-pros-addr', s.prosAddr || '');
  setInputValue('inp-pros-email', s.prosEmail || '');
  setInputValue('inp-pros-phone', s.prosPhone || '');
  setInputValue('inp-def-addr', s.defAddr || '');
  setInputValue('inp-def-email', s.defEmail || '');
  setInputValue('inp-def-phone', s.defPhone || '');
  renderElements();
  callGlobal('renderChargesList');
  renderRecentCases();
}

/**
 * Rebuild the Recent Cases picker only.
 *
 * Split out of syncCaseForm so autosave can refresh the list without rewriting
 * every input: doing that mid-keystroke resets the caret to the end of the box,
 * which is unusable in a text field you are still typing into.
 */
export function renderRecentCases(): void {
  const s = store.getState();
  // Recent cases — limit display to 10 most recent
  const sc = (s.savedCases || []).slice(0, 10);
  const card = byId('recent-cases-card');
  const sel = byId<HTMLSelectElement>('sel-recent-cases');
  if (sc.length > 0) {
    if (card) card.style.display = 'block';
    if (sel) {
      sel.innerHTML =
        '<option value="">-- Select a case --</option>' +
        sc
          .map(
            (c, i) =>
              '<option value="' +
              i +
              '">' +
              escapeHTML(c.caseName || 'Untitled') +
              ' — ' +
              escapeHTML(c.court || '') +
              (c.trialDate ? ' (' + escapeHTML(c.trialDate) + ')' : '') +
              '</option>'
          )
          .join('');
      sel.value = String(s.selectedCaseIdx); // persist selection across rebuilds via state
    }
  } else if (card) {
    card.style.display = 'none';
  }
}

/** Pull every Case-form field into state. Shared by saveCase and the autosave. */
function readCaseForm(): void {
  const s = store.getState();
  s.caseName = inputValue('inp-case-name');
  s.court = inputValue('inp-court');
  // `trialDate` holds the docket's Next Event date; the key is historical and
  // kept because it is persisted in localStorage (see types/state.ts).
  s.trialDate = inputValue('inp-next-event');
  s.judge = inputValue('inp-judge');
  s.opp = inputValue('inp-opp');
  s.defenseAtty = inputValue('inp-defense-atty');
  s.caseNotes = inputValue('inp-case-notes');
  s.docket = inputValue('inp-docket');
  s.county = inputValue('inp-county') || 'Suffolk';
  s.prossBBO = inputValue('inp-pros-bbo');
  s.defBBO = inputValue('inp-def-bbo');
  s.indictmentFacts = inputValue('inp-indictment-facts');
  // chargeDescription is NOT read: #inp-charge-description does not exist in the
  // markup, and inputValue() returns '' for a missing element, so this line
  // blanked the field on every save — including one just restored from a saved
  // case. The Charges tab's list is the record.
  s.prosAddr = inputValue('inp-pros-addr');
  s.prosEmail = inputValue('inp-pros-email');
  s.prosPhone = inputValue('inp-pros-phone');
  s.defAddr = inputValue('inp-def-addr');
  s.defEmail = inputValue('inp-def-email');
  s.defPhone = inputValue('inp-def-phone');
}

/**
 * A case's stable identity.
 *
 * The docket number comes first because it does not change: the caption often
 * does (spelling, "vs." vs "v.", a middle name added on import), and keying on
 * caseName + court alone made every such edit produce a SECOND entry rather
 * than updating the first. With autosave typing into the case-name box would
 * otherwise leave a trail of half-typed cases.
 */
export function caseKey(c: { docket?: unknown; caseName?: unknown; court?: unknown }): string {
  const docket = String(c.docket || '').trim().toLowerCase();
  if (docket) return 'd:' + docket;
  return 'n:' + String(c.caseName || '').trim().toLowerCase() + '|' + String(c.court || '').trim().toLowerCase();
}

/**
 * Write the open case into savedCases, replacing its previous entry.
 *
 * Existing entries are updated IN PLACE. Autosave runs while you type, and
 * unshifting on every keystroke would make the Recent Cases dropdown reorder
 * itself under your finger. Genuinely new cases still go to the top.
 */
function upsertSavedCase(): void {
  const s = store.getState();
  if (!s.caseName && !s.docket) return; // nothing to identify it by yet

  {
    const snap: SavedCase = {
      caseName: s.caseName,
      court: s.court,
      trialDate: s.trialDate,
      judge: s.judge,
      opp: s.opp,
      defenseAtty: s.defenseAtty,
      caseNotes: s.caseNotes,
      chargeDescription: s.chargeDescription,
      crimeElements: (s.crimeElements || []).slice(),
      activeCharges: s.activeCharges.slice(),
      // The actual charge text, which used to be left out of SavedCase entirely.
      // Only the derived slugs were stored, so saving and reopening a case lost
      // what the defendant was charged with and left the previous case's charges
      // on screen.
      chargeList: (s.chargeList || []).map((c) => ({ ...c })),
      savedAt: new Date().toLocaleDateString(),
      docket: s.docket,
      county: s.county || 'Suffolk',
      prossBBO: s.prossBBO,
      defBBO: s.defBBO,
      indictmentFacts: s.indictmentFacts,
      prosAddr: s.prosAddr,
      prosEmail: s.prosEmail,
      prosPhone: s.prosPhone,
      defAddr: s.defAddr,
      defEmail: s.defEmail,
      defPhone: s.defPhone
    };
    if (!s.savedCases) s.savedCases = [];
    const key = caseKey(snap);
    const idx = s.savedCases.findIndex((c) => caseKey(c) === key);
    if (idx >= 0) {
      s.savedCases[idx] = snap;
      s.selectedCaseIdx = idx;
    } else {
      s.savedCases.unshift(snap);
      s.selectedCaseIdx = 0;
      if (s.savedCases.length > 10) s.savedCases = s.savedCases.slice(0, 10);
    }
  }
}

/**
 * Save without being asked.
 *
 * Losing a docket number typed in a courtroom because nobody tapped a button is
 * a worse failure than an over-eager write, so every edit persists. Debounced
 * so a burst of keystrokes is one write, and silent — a toast on every pause
 * would be unbearable. The explicit Save Case button remains for reassurance
 * and forces an immediate write.
 */
let autosaveTimer: ReturnType<typeof setTimeout> | null = null;
function autosaveCase(): void {
  if (autosaveTimer) clearTimeout(autosaveTimer);
  autosaveTimer = setTimeout(() => {
    autosaveTimer = null;
    readCaseForm();
    upsertSavedCase();
    store.save();
    renderRecentCases();
  }, 600);
}

/**
 * Wire autosave to the Case page.
 *
 * One delegated listener rather than a handler per field, so fields added to
 * the markup later are covered without anyone remembering to wire them.
 * `input` catches typing; `change` catches the <select>s and any autofill that
 * does not raise `input`.
 */
export function installCaseAutosave(): void {
  const page = document.getElementById('page-case');
  if (!page) return;
  const onEdit = (e: Event): void => {
    const t = e.target as HTMLElement | null;
    if (!t || !/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
    // The Recent Cases picker is navigation, not a case field: reacting to it
    // would autosave the case being loaded over the one being left.
    if (t.id === 'sel-recent-cases') return;
    autosaveCase();
  };
  page.addEventListener('input', onEdit);
  page.addEventListener('change', onEdit);
}

/** Read the Case form into state, snapshot it into savedCases, and persist. */
export function saveCase(): void {
  if (autosaveTimer) {
    clearTimeout(autosaveTimer);
    autosaveTimer = null;
  }
  readCaseForm();
  upsertSavedCase();
  store.save();
  syncCaseForm();
  renderRecentCases();
  showToast('Case saved');
}

/** Clear all case fields but preserve jurors and savedCases. */
export function newCase(): void {
  const s = store.getState();
  s.caseName = '';
  s.court = 'Suffolk Superior';
  s.county = 'Suffolk';
  s.trialDate = '';
  s.judge = '';
  s.opp = '';
  s.defenseAtty = '';
  s.caseNotes = '';
  s.docket = '';
  s.prossBBO = '';
  s.defBBO = '';
  s.indictmentFacts = '';
  s.chargeDescription = '';
  s.crimeElements = [];
  s.activeCharges = [];
  // The charge list is part of the case, not the app. Leaving it behind meant a
  // new case opened holding the previous defendant's charges.
  s.chargeList = [];
  // A new case is read as yourself: never inherit the last case's borrowed lens.
  s.panelLens = s.side;
  s.prosAddr = '';
  s.prosEmail = '';
  s.prosPhone = '';
  s.defAddr = '';
  s.defEmail = '';
  s.defPhone = '';
  s.selectedCaseIdx = '';
  store.save();
  syncCaseForm();
  const analysisCard = byId('analysis-card');
  if (analysisCard) analysisCard.style.display = 'none';
  const elementsList = byId('elements-list');
  if (elementsList) elementsList.innerHTML = '';
  switchCaseTab('info');
  showToast('New case started');
}

/** Delete the case selected in the recent-cases dropdown, clearing the form. */
export async function deleteSelectedCase(): Promise<void> {
  const s = store.getState();
  // Always read live from the DOM — state.selectedCaseIdx can be stale
  const sel = byId<HTMLSelectElement>('sel-recent-cases');
  const rawVal: string = sel ? sel.value : String(s.selectedCaseIdx);
  const idx = parseInt(rawVal, 10);
  if (rawVal === '' || isNaN(idx) || idx < 0) {
    showToast('Select a case from the dropdown first, then tap Delete');
    return;
  }
  if (!(s.savedCases && s.savedCases[idx])) {
    showToast('Case not found — try reloading');
    return;
  }
  const name = s.savedCases[idx].caseName || 'Untitled';
  const ok = await confirmApp(
    'Delete Case',
    'Permanently delete "' +
      name +
      '"? All data saved under this case will be removed. This cannot be undone.',
    'Delete',
    'btn-danger'
  );
  if (!ok) {
    showToast('Deletion canceled');
    return;
  }
  // Remove the case and wipe all its data from current state if it was loaded
  s.savedCases.splice(idx, 1);
  s.selectedCaseIdx = '';
  // Also clear the live form fields so no ghost data remains
  s.caseName = '';
  s.court = 'Suffolk Superior';
  s.county = 'Suffolk';
  s.trialDate = '';
  s.judge = '';
  s.opp = '';
  s.defenseAtty = '';
  s.caseNotes = '';
  s.statute = '';
  s.chargeDescription = '';
  s.activeCharges = [];
  s.crimeElements = [];
  s.indictmentFacts = '';
  s.docket = '';
  s.courtSpecific = '';
  s.prossBBO = '';
  s.defBBO = '';
  s.prosAddr = '';
  s.prosEmail = '';
  s.prosPhone = '';
  s.defAddr = '';
  s.defEmail = '';
  s.defPhone = '';
  s.motionSavedText = '';
  s.attyAffSavedText = '';
  store.save();
  syncCaseForm();
  const analysisCard = byId('analysis-card');
  if (analysisCard) analysisCard.style.display = 'none';
  const elementsList = byId('elements-list');
  if (elementsList) elementsList.innerHTML = '';
  showToast('"' + name + '" deleted and form cleared');
}

/** Load a saved case (by savedCases index) into the live form. */
export function loadSavedCase(idx: number | string): void {
  if (idx === '' || idx === undefined) return;
  const s = store.getState();
  const i = typeof idx === 'number' ? idx : parseInt(idx, 10);
  s.selectedCaseIdx = i;
  const c = (s.savedCases || [])[i];
  if (!c) return;
  s.caseName = c.caseName || '';
  s.court = c.court || 'Suffolk Superior';
  s.trialDate = c.trialDate || '';
  s.judge = c.judge || '';
  s.opp = str(c.opp);
  s.defenseAtty = str(c.defenseAtty);
  s.caseNotes = str(c.caseNotes);
  s.chargeDescription = str(c.chargeDescription);
  s.docket = c.docket || '';
  s.prossBBO = str(c.prossBBO);
  s.defBBO = str(c.defBBO);
  s.indictmentFacts = str(c.indictmentFacts);
  s.prosAddr = str(c.prosAddr);
  s.prosEmail = str(c.prosEmail);
  s.prosPhone = str(c.prosPhone);
  s.defAddr = str(c.defAddr);
  s.defEmail = str(c.defEmail);
  s.defPhone = str(c.defPhone);
  if (c.activeCharges) s.activeCharges = c.activeCharges.slice();
  // Unconditional, and empty when the saved case predates chargeList being
  // stored: an empty Charges tab is recoverable from the docket, whereas
  // silently keeping the last case's charges is not detectable at all.
  const cl = c.chargeList;
  s.chargeList = Array.isArray(cl) ? (cl as ChargeDescription[]).map((x) => ({ ...x })) : [];
  const ce = c.crimeElements;
  if (Array.isArray(ce)) s.crimeElements = ce.slice() as string[];
  syncCaseForm();
  showToast('Loaded: ' + s.caseName);
}

/** Render the read-only Elements of Crimes list (auto-generated by analyzeCase). */
export function renderElements(): void {
  const el = store.getState().crimeElements || [];
  const list = byId('elements-list');
  if (!list) return;
  if (!el.length) {
    list.innerHTML =
      '<div style="font-size:11px;color:var(--text-muted);font-style:italic;">No elements generated yet. Enter a statute/charge above and tap Analyze.</div>';
    return;
  }
  list.innerHTML = el
    .map(
      (e, i) =>
        '<div style="display:flex;align-items:flex-start;gap:6px;">' +
        '<span style="font-size:11px;font-weight:700;color:var(--blue-primary);min-width:18px;margin-top:2px;">' +
        (i + 1) +
        '.</span>' +
        '<span style="font-size:11px;color:var(--text-secondary);flex:1;line-height:1.6;">' +
        escapeHTML(e) +
        '</span>' +
        '</div>'
    )
    .join('');
  callGlobal('applyCitationLinks', list);
}

/** Switch between the Case tab's Info / Parties / Charges / Attorneys sub-tabs. */
export function switchCaseTab(tab: string): void {
  saveCase();
  const tabs: Record<string, string> = {
    info: 'case-tab-info',
    parties: 'case-tab-parties',
    attorneys: 'case-tab-attorneys',
    charges: 'case-tab-charges'
  };
  Object.keys(tabs).forEach((k) => {
    const el = byId(tabs[k]);
    if (el) el.style.display = k === tab ? 'flex' : 'none';
    // Selected state is a class, not inline styles — appearance belongs in
    // .ctab / .ctab.active so retinting the tabs is a CSS-only change.
    byId('ctab-' + k)?.classList.toggle('active', k === tab);
  });
  const analyzeBtn = byId('btn-analyze');
  if (analyzeBtn) analyzeBtn.style.display = tab === 'charges' ? 'flex' : 'none';
  if (tab === 'charges') callGlobal('renderChargesList');
  // Parties used to be its own page with an onPageEnter hook; now that it is a
  // sub-tab, rendering has to be driven from here instead.
  if (tab === 'parties') callGlobal('renderPartiesPage');
}
